class HeaderSearch extends HTMLElement {
  constructor() {
    super();
    this.searchOpen = false;
    this.predictiveSearch = null;
  }

  connectedCallback() {
    this.searchToggle = this.querySelector('[data-search-toggle]');
    this.searchComponent = this.querySelector('[data-search-component]');
    this.searchInput = this.querySelector('[data-search-input]');
    this.closeButton = this.querySelector('[data-search-close]');
    this.form = this.querySelector('form');
    
    const predictiveSearchEnabled = this.dataset.predictiveSearchEnabled === 'true';
    const searchResources = this.dataset.searchResources || '';

    if (predictiveSearchEnabled && searchResources) {
      this.predictiveSearch = new PredictiveSearch(this, searchResources);
    }

    this.setupEventListeners();
  }

  setupEventListeners() {
    // Toggle search
    if (this.searchToggle) {
      this.searchToggle.addEventListener('click', (e) => {
        e.preventDefault();
        this.openSearch();
      });

      this.searchToggle.addEventListener('keydown', (e) => {
        if (e.code === 'Space') {
          e.preventDefault();
          this.openSearch();
        }
      });
    }

    // Close button
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => {
        this.closeSearch();
      });
    }

    // Close on outside click
    this.outsideClickHandler = (e) => {
      if (this.searchOpen && !this.contains(e.target) && !this.searchToggle?.contains(e.target)) {
        this.closeSearch();
      }
    };
    document.addEventListener('click', this.outsideClickHandler);

    // Close on escape
    this.escapeKeyHandler = (e) => {
      if (e.code === 'Escape' && this.searchOpen) {
        e.preventDefault();
        e.stopPropagation();
        this.closeSearch(true, this.searchToggle);
      }
    };
    document.addEventListener('keydown', this.escapeKeyHandler);

    // Close on focus outside
    this.focusInHandler = (e) => {
      if (this.searchOpen && !this.contains(e.target) && !this.searchToggle?.contains(e.target)) {
        this.closeSearch();
      }
    };
    document.addEventListener('focusin', this.focusInHandler);

    // Form submission
    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        if (this.predictiveSearch) {
          const selectedOption = this.querySelector('[aria-selected="true"] a');
          if (selectedOption) {
            e.preventDefault();
            selectedOption.click();
          } else if (!this.predictiveSearch.trimmedQuery.length) {
            e.preventDefault();
          }
        }
      });
    }
  }

  openSearch() {
    this.searchOpen = true;
    this.updateVisibility();
    
    // Focus on the specific input with id search-logo_left_menu_left-mobile
    requestAnimationFrame(() => {
      const targetInput = document.getElementById('search-logo_left_menu_left-mobile');
      if (targetInput) {
        targetInput.focus();
        targetInput.click();
      } else if (this.searchInput) {
        // Fallback to current input if target not found
        this.searchInput.click();
        this.searchInput.focus();
      }
    });

    // Set search bar height
    requestAnimationFrame(() => {
      if (this.searchComponent) {
        const height = this.searchComponent.getBoundingClientRect().height;
        this.searchComponent.style.setProperty('--search-bar-height', `${height}px`);
      }
    });

    if (this.predictiveSearch) {
      this.predictiveSearch.onOpen();
    }
  }

  closeSearch(clearSearchTerm = false, focusAfter = null) {
    this.searchOpen = false;
    this.updateVisibility();

    if (this.predictiveSearch) {
      this.predictiveSearch.closeResults(clearSearchTerm);
    }

    if (focusAfter) {
      setTimeout(() => {
        focusAfter.focus();
      }, 0);
    }
  }

  disconnectedCallback() {
    // Clean up event listeners
    if (this.outsideClickHandler) {
      document.removeEventListener('click', this.outsideClickHandler);
    }
    if (this.focusInHandler) {
      document.removeEventListener('focusin', this.focusInHandler);
    }
    if (this.escapeKeyHandler) {
      document.removeEventListener('keydown', this.escapeKeyHandler);
    }
  }

  updateVisibility() {
    if (this.searchComponent) {
      if (this.searchOpen) {
        this.searchComponent.style.display = '';
        // Trigger transition
        requestAnimationFrame(() => {
          this.searchComponent.style.opacity = '1';
        });
      } else {
        this.searchComponent.style.opacity = '0';
        // Wait for transition before hiding
        setTimeout(() => {
          if (!this.searchOpen) {
            this.searchComponent.style.display = 'none';
          }
        }, 200);
      }
    }

    // Update input aria-expanded
    if (this.searchInput && this.predictiveSearch) {
      this.searchInput.setAttribute(
        'aria-expanded',
        this.predictiveSearch.resultsOpen ? 'true' : 'false'
      );
    }
  }
}

class PredictiveSearch {
  constructor(headerSearch, resources) {
    this.headerSearch = headerSearch;
    this.resources = resources;
    this.loading = false;
    this.resultsOpen = false;
    this.rawQuery = '';
    this.resultsHTML = null;
    this.selectedElement = null;
    this.allOptionsArray = null;
    this.debounceTimeout = null;

    this.resultsContainer = headerSearch.querySelector('[data-predictive-results]');
    this.input = headerSearch.querySelector('[data-search-input]');
    this.statusElement = headerSearch.querySelector('[data-predictive-status]');
    
    // Initialize input aria attributes
    if (this.input) {
      this.input.setAttribute('aria-expanded', 'false');
      // Get initial value if input already has text (e.g., from previous search)
      this.rawQuery = this.input.value || '';
    }

    this.setupEventListeners();
  }

  setupEventListeners() {
    if (!this.input) return;

    // Input changes with debounce
    this.input.addEventListener('input', (e) => {
      this.rawQuery = e.target.value;
      this.handleQueryChange();
    });

    // Focus handler
    this.input.addEventListener('focus', () => {
      this.onFocus();
    });

    // Keyboard navigation
    this.input.addEventListener('keydown', (e) => {
      this.onKeydown(e);
    });

    this.input.addEventListener('keyup', (e) => {
      this.onKeyup(e);
    });


    if (!this.resultsContainer) return;

    // Trap focus when results are open
    this.resultsContainer.addEventListener('keydown', (e) => {
      if (e.code === 'Tab' && this.resultsOpen) {
        e.preventDefault();
        // Trap focus within results
        const focusableElements = this.resultsContainer.querySelectorAll(
          'a, button, [tabindex]:not([tabindex="-1"])'
        );
        if (focusableElements.length > 0) {
          if (e.shiftKey) {
            focusableElements[focusableElements.length - 1].focus();
          } else {
            focusableElements[0].focus();
          }
        }
      }
    });
}

  get trimmedQuery() {
    return this.rawQuery.trim();
  }

  handleQueryChange() {
    // Show results container immediately when user starts typing
    if (this.trimmedQuery.length) {
      this.openResults();
    }

    // Clear previous debounce
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }

    // Debounce the search
    this.debounceTimeout = setTimeout(() => {
      if (!this.trimmedQuery.length) {
        this.closeResults();
      } else {
        // Ensure results are open (in case they weren't opened above)
        this.openResults();
        this.getSearchResults();
      }
    }, 300);
  }

  async getSearchResults() {
    if (!this.trimmedQuery.length) return;

    this.loading = true;
    this.updateLoadingState();

    if (window.liveRegion) {
      window.liveRegion(window.theme.strings.loading);
    }

    try {
      const url = getURLWithParams(window.theme.routes.predictive_search_url, {
        q: this.trimmedQuery,
        'resources[type]': this.resources,
        section_id: 'predictive-search',
      });

      const updatedHTML = await fetchHTML(url);
      const resultsMarkup = querySelectorInHTMLString(
        '#shopify-section-predictive-search',
        updatedHTML
      ).innerHTML;

      const liveRegionText = querySelectorInHTMLString(
        '#predictive-search-count',
        updatedHTML
      ).innerHTML;

      this.resultsHTML = resultsMarkup;
      
      if (window.liveRegion && liveRegionText) {
        window.liveRegion(liveRegionText);
      }

      this.updateResults();
      this.loading = false;
      this.updateLoadingState();
      this.openResults();
    } catch (error) {
      console.error('Error fetching search results:', error);
      this.loading = false;
      this.updateLoadingState();
    }
  }

  updateResults() {
    if (!this.resultsContainer) return;

    const resultsHTML = this.resultsContainer.querySelector('[data-results-html]');
    if (resultsHTML && this.resultsHTML) {
      resultsHTML.innerHTML = this.resultsHTML;
      resultsHTML.style.display = '';
      
      // Update options array
      this.allOptionsArray = Array.from(
        this.resultsContainer.querySelectorAll('[role="option"]')
      );
    }
  }

  updateLoadingState() {
    if (!this.resultsContainer) return;

    const loadingElement = this.resultsContainer.querySelector('[data-loading]');
    if (loadingElement) {
      loadingElement.style.display = this.loading ? '' : 'none';
    }

    const resultsElement = this.resultsContainer.querySelector('[data-results-html]');
    if (resultsElement) {
      resultsElement.style.display = this.loading ? 'none' : '';
    }
  }

  onFocus() {
    this.openResults();

    // Open results immediately when input is focused, even if no query yet
    // This ensures the drawer is ready when user starts typing
    if (this.trimmedQuery.length && !this.resultsHTML) {
        this.getSearchResults();
    } 

  }

  onKeydown(e) {
    // Prevent cursor movement in input when using arrow keys
    // Prevent form submission if pressing Enter on a selected element
    if (
      e.code === 'ArrowUp' ||
      e.code === 'ArrowDown' ||
      (e.code === 'Enter' && this.selectedElement)
    ) {
      e.preventDefault();
    }
  }

  onKeyup(e) {
    e.preventDefault();

    switch (e.code) {
      case 'ArrowUp':
        this.switchOption('previous');
        break;
      case 'ArrowDown':
        this.switchOption('next');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  switchOption(direction) {
    if (direction === 'previous' && !this.selectedElement) return;

    if (!this.allOptionsArray || this.allOptionsArray.length === 0) return;

    if (direction === 'next') {
      this.selectedElement = nextOrFirst(
        this.allOptionsArray,
        this.selectedElement
      );
    } else if (direction === 'previous') {
      this.selectedElement = previousOrLast(
        this.allOptionsArray,
        this.selectedElement
      );
    }

    if (this.selectedElement) {
      // Update aria-selected attributes
      this.allOptionsArray.forEach((el) => {
        el.setAttribute('aria-selected', el === this.selectedElement ? 'true' : 'false');
      });

      this.selectedElement.scrollIntoView({
        behavior: isMotionSafe() ? 'smooth' : 'auto',
        block: 'end',
      });
    }
  }

  selectOption() {
    if (this.selectedElement) {
      const selectedOptionAction = this.selectedElement.querySelector('a, button');
      if (selectedOptionAction) {
        selectedOptionAction.click();
      }
    }
  }

  openResults() {
    this.resultsOpen = true;
    if (this.resultsContainer) {
      // Ensure the results container is visible
      this.resultsContainer.style.display = '';
      // Force a reflow to ensure display change takes effect immediately
      void this.resultsContainer.offsetHeight;
    }
    if (this.input) {
      this.input.setAttribute('aria-expanded', 'true');
    }
    // Lock scroll when results are open and we have content to show
    if (this.trimmedQuery.length || this.resultsHTML) {
      document.documentElement.style.overflowY = 'hidden';
    }
  }

  closeResults(clearSearchTerm = false) {
    this.resultsOpen = false;
    if (this.resultsContainer) {
      this.resultsContainer.style.display = 'none';
    }
    if (this.input) {
      this.input.setAttribute('aria-expanded', 'false');
    }

    if (clearSearchTerm && this.input) {
      this.rawQuery = '';
      this.input.value = '';
    }

    this.selectedElement = null;
    if (this.allOptionsArray) {
      this.allOptionsArray.forEach((el) => {
        el.setAttribute('aria-selected', 'false');
      });
    }

    document.documentElement.style.overflowY = 'auto';
  }

  onOpen() {
    // When search opens, if there's already a query, show results immediately
    if (this.trimmedQuery.length) {
      this.openResults();
      requestAnimationFrame(() => {
        this.getSearchResults();
      });
    }
    // Even without a query, ensure the results container is ready
    // This helps with the visual feedback when user starts typing
  }
}

customElements.define('header-search', HeaderSearch);
